import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import {
  DirectionsCar,
  LocalGasStation,
  TrendingUp,
  Download,
  Speed,
  CurrencyRupeeOutlined,
} from "@mui/icons-material";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import NavigationHeader from "../../components/NavigationHeader";
import FuelReportsFilters from "../../components/fuel-reports/FuelReportsFilters";
import {
  FuelReportsProvider,
  useFuelReports,
} from "../../contexts/FuelReportsContext";
import {
  DEFAULT_VEHICLE_REPORT_DATA_YEAR,
  fuelReportMonthKey,
} from "../../utils/fuelReportPeriodUtils";
import {
  FuelReportsService,
  VehicleConsumptionData,
} from "../../services/fuelReportsService";
import { formatNumber } from "../../utils/formatters";
import { formatCurrency } from "../../utils/utils";
import { useResponsiveBreakpoints } from "../../utils/responsiveUtils";
import {
  commonTypographyProps,
  commonButtonProps,
  tableHeaderStyles,
  tableCellStyles,
  tableRowStyles,
  commonTableContainerProps,
} from "../../utils/styles";

/** Coerce API string/number metrics for Indian-locale grouping (e.g. fuel totals from `.toFixed`). */
function formatFleetMetric(
  value: number | string | undefined,
  decimals: number
): string {
  const n = typeof value === "string" ? parseFloat(value) : Number(value);
  if (value === "" || value === null || value === undefined || Number.isNaN(n)) {
    return formatNumber(0, decimals);
  }
  return formatNumber(n, decimals);
}

const EFFICIENCY_BAND_ORDER = [
  "excellent",
  "good",
  "average",
  "poor",
  "no_data",
] as const;

const EFFICIENCY_BAND_LABEL: Record<(typeof EFFICIENCY_BAND_ORDER)[number], string> =
  {
    excellent: "Excellent",
    good: "Good",
    average: "Average",
    poor: "Poor",
    no_data: "No data",
  };

const EFFICIENCY_BAND_COLOR: Record<
  (typeof EFFICIENCY_BAND_ORDER)[number],
  string
> = {
  excellent: "#2e7d32",
  good: "#689f38",
  average: "#f57c00",
  poor: "#c62828",
  no_data: "#757575",
};

type SortOrder = "asc" | "desc";
type SortField = keyof VehicleConsumptionData | "";

const VehicleConsumptionReportContent: React.FC = () => {
  const { filters, isValidDateRange } = useFuelReports();
  const { isMobile } = useResponsiveBreakpoints();

  const [sortField, setSortField] = useState<SortField>("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  const queryEnabled =
    Boolean(filters.dateFilter) &&
    (filters.dateFilter !== "custom" ||
      (Boolean(filters.startDate) &&
        Boolean(filters.endDate) &&
        isValidDateRange(filters.startDate, filters.endDate)));

  const {
    data: summaryResponse,
    isPending: summaryPending,
    isError: summaryError,
    error: summaryErr,
    refetch: refetchSummary,
    isFetching: summaryFetching,
  } = useQuery({
    queryKey: ["vehicle-consumption-report", "summary", filters],
    queryFn: () =>
      FuelReportsService.getVehicleConsumption(filters, { summaryOnly: true }),
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const {
    data: detailsResponse,
    isPending: detailsPending,
    isError: detailsError,
    error: detailsErr,
    refetch: refetchDetails,
    isFetching: detailsFetching,
  } = useQuery({
    queryKey: ["vehicle-consumption-report", "details", filters],
    queryFn: () => FuelReportsService.getVehicleConsumption(filters),
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const summary = summaryResponse?.data?.summary;
  const detailRows = detailsResponse?.data?.data ?? [];

  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortField(field);
  };

  const sortedData = React.useMemo(() => {
    if (!detailRows.length || !sortField) return detailRows;

    return [...detailRows].sort((a, b) => {
      const aVal = a[sortField as keyof VehicleConsumptionData];
      const bVal = b[sortField as keyof VehicleConsumptionData];

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return 0;
    });
  }, [detailRows, sortField, sortOrder]);

  const handleExport = async () => {
    try {
      const blob = await FuelReportsService.exportReport({
        ...filters,
        reportType: "vehicle",
      });
      FuelReportsService.downloadCSV(blob, "vehicle-consumption-report.csv");
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const getEfficiencyColor = (rating: string) => {
    if (rating.includes("Excellent")) return "#4CAF50";
    if (rating.includes("Good")) return "#8BC34A";
    if (rating.includes("Average")) return "#FF9800";
    if (rating.includes("Poor")) return "#F44336";
    return "#9E9E9E";
  };

  const renderSummaryCards = () => {
    if (!summary) return null;

    const kpiCardSx = {
      height: "100%",
      border: 1,
      borderColor: "divider",
      borderRadius: 2,
      boxShadow: "none",
      bgcolor: "background.paper",
    } as const;

    const items: {
      label: string;
      value: React.ReactNode;
      Icon: React.ComponentType<{ sx?: object }>;
      iconBg: string;
    }[] = [
      {
        label: "Fleet size",
        value: formatFleetMetric(summary.total_vehicles, 0),
        Icon: DirectionsCar,
        iconBg: "#1565c0",
      },
      {
        label: "Total fuel volume",
        value: (
          <>
            {formatFleetMetric(summary.total_fuel_consumed, 1)}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500, ml: 0.5, fontSize: "0.95rem" }}
            >
              L
            </Typography>
          </>
        ),
        Icon: LocalGasStation,
        iconBg: "#ef6c00",
      },
      {
        label: "Total fuel spend",
        value: formatCurrency(summary.total_fuel_cost),
        Icon: CurrencyRupeeOutlined,
        iconBg: "#2e7d32",
      },
      {
        label: "Fleet average efficiency",
        value: (
          <>
            {formatFleetMetric(summary.avg_efficiency, 1)}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500, ml: 0.5, fontSize: "0.95rem" }}
            >
              km/l
            </Typography>
          </>
        ),
        Icon: Speed,
        iconBg: "#6a1b9a",
      },
    ];

    return (
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        {items.map(({ label, value, Icon, iconBg }) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <Card variant="outlined" sx={kpiCardSx}>
              <CardContent
                sx={{
                  p: 2.25,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  "&:last-child": { pb: 2.25 },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar
                    sx={{
                      bgcolor: iconBg,
                      width: 44,
                      height: 44,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    }}
                  >
                    <Icon sx={{ fontSize: "1.35rem" }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        lineHeight: 1.3,
                        letterSpacing: "0.06em",
                        fontWeight: 600,
                        fontSize: "0.68rem",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.2rem", sm: "1.35rem" },
                        lineHeight: 1.25,
                        letterSpacing: "-0.02em",
                        color: "text.primary",
                        mt: 0.25,
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderEfficiencyDistribution = () => {
    if (!summary?.efficiency_distribution) return null;

    const distribution = summary.efficiency_distribution;
    const total = Object.values(distribution).reduce(
      (sum, count) => sum + count,
      0
    );

    return (
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderColor: "divider",
          borderRadius: 2,
          boxShadow: "none",
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <TrendingUp sx={{ fontSize: "1.25rem", color: "primary.main" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
                Efficiency mix
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
              Share of fleet by efficiency band. A large &quot;No data&quot; share
              usually means missing odometer readings-worth a follow-up with ops.
            </Typography>
          </Box>
          <Stack spacing={2}>
            {EFFICIENCY_BAND_ORDER.map((key) => {
              const count = distribution[key] ?? 0;
              const pct = total > 0 ? (count / total) * 100 : 0;
              const barColor = EFFICIENCY_BAND_COLOR[key];
              return (
                <Box key={key}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      {EFFICIENCY_BAND_LABEL[key]}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
                    >
                      {formatNumber(count, 0)} vehicles · {pct.toFixed(1)}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, pct)}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 1,
                        bgcolor: barColor,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const vehicleCountLabel =
    summary?.total_vehicles ?? sortedData.length ?? 0;

  return (
    <Box>
      <Box sx={{ position: "relative", mb: 3 }}>
        {summary && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: "0.12em", fontSize: "0.7rem" }}
            >
              Executive summary
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mt: 0.25, letterSpacing: "-0.02em" }}
            >
              Fleet overview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
              High-level KPIs for the applied filters. Detailed vehicle rows load
              below.
            </Typography>
          </Box>
        )}
        {summaryFetching && summary && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              zIndex: 1,
              borderRadius: 1,
            }}
          />
        )}
        {summaryError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => refetchSummary()}>
                Retry
              </Button>
            }
          >
            Could not load summary:{" "}
            {summaryErr instanceof Error ? summaryErr.message : "Unknown error"}
          </Alert>
        )}
        {summaryPending && !summary && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 160,
            }}
          >
            <CircularProgress size={36} />
          </Box>
        )}
        {renderSummaryCards()}
        {renderEfficiencyDistribution()}
      </Box>

      {/* Data Table */}
      <Card sx={{ position: "relative" }}>
        {detailsFetching && detailRows.length > 0 && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              zIndex: 2,
              borderRadius: "4px 4px 0 0",
            }}
          />
        )}
        <CardContent sx={{ p: 1.5 }}>
          {detailsError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => refetchDetails()}
                >
                  Retry
                </Button>
              }
            >
              Could not load vehicle list:{" "}
              {detailsErr instanceof Error
                ? detailsErr.message
                : "Unknown error"}
            </Alert>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
              flexDirection: isMobile ? "column" : "row",
              gap: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: "0.8rem" }}>
              Vehicle Consumption Details ({vehicleCountLabel} vehicles)
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              {...commonButtonProps}
            >
              Export CSV
            </Button>
          </Box>

          {detailsPending && detailRows.length === 0 && !detailsError && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 280,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}

          <TableContainer
            component={Paper}
            sx={{
              maxHeight: isMobile ? 400 : 600,
              ...commonTableContainerProps,
              display:
                detailsPending && detailRows.length === 0 && !detailsError
                  ? "none"
                  : undefined,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ ...tableRowStyles }}>
                  <TableCell sx={{ ...tableHeaderStyles }}>
                    <TableSortLabel
                      active={sortField === "vehicleNumber"}
                      direction={
                        sortField === "vehicleNumber" ? sortOrder : "asc"
                      }
                      onClick={() => handleSort("vehicleNumber")}
                      sx={{ ...commonTypographyProps }}
                    >
                      Vehicle
                    </TableSortLabel>
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ ...tableHeaderStyles }}>Model</TableCell>
                  )}
                  <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                    <TableSortLabel
                      active={sortField === "total_fuel_consumed_liters"}
                      direction={
                        sortField === "total_fuel_consumed_liters"
                          ? sortOrder
                          : "asc"
                      }
                      onClick={() => handleSort("total_fuel_consumed_liters")}
                      sx={{ ...commonTypographyProps }}
                    >
                      Fuel (L)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                    <TableSortLabel
                      active={sortField === "total_fuel_cost_inr"}
                      direction={
                        sortField === "total_fuel_cost_inr" ? sortOrder : "asc"
                      }
                      onClick={() => handleSort("total_fuel_cost_inr")}
                      sx={{ ...commonTypographyProps }}
                    >
                      Cost
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                    <TableSortLabel
                      active={sortField === "fuel_efficiency_kmpl"}
                      direction={
                        sortField === "fuel_efficiency_kmpl" ? sortOrder : "asc"
                      }
                      onClick={() => handleSort("fuel_efficiency_kmpl")}
                      sx={{ ...commonTypographyProps }}
                    >
                      Efficiency
                    </TableSortLabel>
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                        Distance
                      </TableCell>
                      <TableCell align="center" sx={{ ...tableHeaderStyles }}>
                        Cost/km
                      </TableCell>
                      <TableCell sx={{ ...tableHeaderStyles }}>Staff</TableCell>
                      <TableCell sx={{ ...tableHeaderStyles }}>
                        Location
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((vehicle, index) => (
                  <TableRow
                    key={`${vehicle.vehicleNumber}-${index}`}
                    hover
                    sx={{ ...tableRowStyles }}
                  >
                    <TableCell sx={{ ...tableCellStyles }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "medium",
                            ...commonTypographyProps,
                          }}
                        >
                          {vehicle.vehicleNumber}
                        </Typography>
                        {isMobile && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {vehicle.model} • {vehicle.vehicleType}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {!isMobile && (
                      <TableCell sx={{ ...tableCellStyles }}>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ ...commonTypographyProps }}
                          >
                            {vehicle.model}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {vehicle.vehicleType}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ ...tableCellStyles }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "medium", ...commonTypographyProps }}
                      >
                        {formatNumber(vehicle.total_fuel_consumed_liters, 1)}
                      </Typography>
                      {isMobile && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {vehicle.fuel_transactions} fills
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ ...tableCellStyles }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "medium", ...commonTypographyProps }}
                      >
                        {formatCurrency(vehicle.total_fuel_cost_inr)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ ...tableCellStyles }}>
                      <Tooltip title={vehicle.efficiency_rating}>
                        <Chip
                          label={`${formatNumber(
                            vehicle.fuel_efficiency_kmpl,
                            1
                          )} km/l`}
                          size="small"
                          sx={{
                            bgcolor: getEfficiencyColor(
                              vehicle.efficiency_rating
                            ),
                            color: "white",
                            fontWeight: "medium",
                            ...commonTypographyProps,
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell align="center" sx={{ ...tableCellStyles }}>
                          <Typography
                            variant="body2"
                            sx={{ ...commonTypographyProps }}
                          >
                            {formatNumber(vehicle.distance_traveled_km)} km
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ ...tableCellStyles }}>
                          <Typography
                            variant="body2"
                            sx={{ ...commonTypographyProps }}
                          >
                            {formatCurrency(vehicle.cost_per_km)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...tableCellStyles }}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ ...commonTypographyProps }}
                            >
                              {vehicle.staff_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              {vehicle.staff_type}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...tableCellStyles }}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ ...commonTypographyProps }}
                            >
                              {vehicle.district}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              {vehicle.zone} • {vehicle.region}
                            </Typography>
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {sortedData.length === 0 && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ ...commonTypographyProps }}
              >
                No vehicle consumption data found for the selected filters.
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, fontSize: "0.7rem" }}
              >
                Try adjusting your date range or removing some filters.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const defaultVehicleReportFilters = {
  dateFilter: fuelReportMonthKey(DEFAULT_VEHICLE_REPORT_DATA_YEAR, 1),
  startDate: "",
  endDate: "",
};

const VehicleConsumptionReport: React.FC = () => {
  return (
    <FuelReportsProvider initialFilters={defaultVehicleReportFilters}>
      <Container maxWidth={false}>
        <Box sx={{ py: 2 }}>
          <NavigationHeader
            title="Vehicle Fuel Consumption Report"
            backNavigationEnabled={true}
          />
          <FuelReportsFilters
            onApplyFilters={() => {}}
            periodPresetMode="monthQuarter"
            dataYear={DEFAULT_VEHICLE_REPORT_DATA_YEAR}
          />

          <VehicleConsumptionReportContent />
        </Box>
      </Container>
    </FuelReportsProvider>
  );
};

export default VehicleConsumptionReport;
